// Search Page - Product catalog for engineers
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  Search as SearchIcon,
  Package,
  History,
  Clock,
  X,
} from 'lucide-react'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { CategoryBrowse } from '../../components/engineer/CategoryBrowse'
import { BranchPriorityPills } from '../../components/engineer/BranchPriorityPills'
import { BranchPriorityNoMatches } from '../../components/engineer/BranchPriorityNotices'
import { LinkedProductCard } from '../../components/engineer/LinkedProductCard'
import { useCategories } from '../../hooks/useCategories'
import {
  AddToCartButton,
  Input,
  Button,
  SearchProductCard,
  Pagination,
  SearchFeedbackModal,
  SearchSuggestionsDropdown,
  useToast,
} from '../../components/radix'
import type { SearchCardStockState } from '../../components/radix'
import { usePostcodeSync } from '../../hooks/usePostcodeSync'
import { useDebounce } from '../../hooks/useDebounce'
import { useBranchPriority } from '../../hooks/useBranchPriority'
import { api } from '../../services/api'
import {
  ProductViewSource,
  trackSearchPerformed,
  trackSearchResultClicked,
  trackSupplierBranchFilterApplied,
} from '../../lib/analytics'
import { mintUuid } from '../../lib/checkout-session'
import {
  buildPrioritisedEntries,
  buildPriorityInfoByMaster,
  pickPrioritisedBranchId,
} from '../../utils/branchPriority'
import type {
  BranchPriorityInfo,
  LinkedProductCardInfo,
  PrioritisedResultEntry,
} from '../../utils/branchPriority'
import type { BasketBranch } from '../../hooks/useBranchPriority'
import type { CartFulfillmentMethod } from '../../utils/cartFulfillment'
import type { Category, MasterProduct, RecentPurchase, SearchSuggestion } from '../../types'
import { IN_STOCK_LABEL } from '../../constants/display'
import { QUICK_ADD_METHOD_PARAM } from '../../constants/quickAdd'

// Keeps "no nearby branch" masters pinned below every reachable result in the
// client-side price/distance re-sorts, mirroring the server-side ordering.
// Returns a comparator delta when the two differ, or 0 when both are the same
// reachability so the caller's own sort key decides.
const farBranchLast = (a: MasterProduct, b: MasterProduct): number => {
  const fa = a.hasNearbyBranch === false ? 1 : 0
  const fb = b.hasNearbyBranch === false ? 1 : 0
  return fa - fb
}

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

/**
 * Render an OpenSearch phrase-suggester highlight string as React nodes.
 *
 * The backend wraps corrected tokens in <em>…</em> exactly as configured.
 * Parsing the tags explicitly avoids dangerouslySetInnerHTML: the content
 * between the tags is plain tokenised text (standard tokenizer strips HTML
 * from indexed display names), so there is nothing dangerous to render, but
 * using raw HTML injection for backend-provided strings is poor hygiene
 * regardless. This function only recognises <em> — any other markup is
 * rendered verbatim as text.
 *
 * Example: "<em>steel</em> <em>pipe</em>" →
 *   [<em>steel</em>, " ", <em>pipe</em>]
 */
function renderHighlighted(html: string) {
  const parts = html.split(/(<em>[^<]*<\/em>)/g)
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('<em>') && part.endsWith('</em>') ? (
          <em key={i} className="not-italic font-bold">
            {part.slice(4, -5)}
          </em>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}

// Format time ago
function formatTimeAgo(dateStr: string) {
  const date = new Date(dateStr)
  const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 14) return '1 week ago'
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`
  return '1 month ago'
}

// React Query staleTime for the per-supplier stock-check responses. Matches
// the backend's per-(supplier, branch, outward) cache TTL ballpark so a
// follow-up search for the same postcode reuses the resolved pills instead
// of re-firing every supplier call.
const STOCK_CHECK_STALE_MS = 60 * 1000

// Hard frontend timeout for any single supplier's stock-check request. A
// supplier that hangs past this resolves the pill as "doesn't vote" so the
// card never stays in "Checking…" forever. The backend HTTP client may
// still complete the call in the background and populate cache for the
// next search.
const STOCK_CHECK_SUPPLIER_TIMEOUT_MS = 15 * 1000

// Page size for client-side in-stock pagination
const IN_STOCK_PAGE_SIZE = 20

// ── search_id: the analytics correlation id for one search ──────────────────
//
// search_performed and every search_result_clicked it produces share one
// search_id, so a click traces to its exact originating search rather than to
// a keyword — which is ambiguous once the same keyword is searched more than
// once. Held at module scope so it survives this page unmounting and
// remounting (opening a result navigates to the sibling ProductDetail route
// and back), which would otherwise mint a new id and re-emit the search.
//
// One id per distinct search, keyed on query + category (NOT page): turning
// pages and returning from a product reuse it, and a changed query or category
// scope mints a fresh one. commitSearch already treats an identical re-submit
// as a no-op, so "new search" here is exactly "the key changed" — and two
// searches for the same keyword separated by another search get different ids
// because the key moves in between. search_performed is de-duplicated by this
// id (not by keyword), so a genuine repeat search is counted as a new one.
let currentSearch: { key: string; id: string } | null = null
const reportedSearchIds = new Set<string>()

/**
 * Stable key identifying one search execution. It mirrors every dimension of
 * the search's React Query key that changes the result set — anything that
 * makes the app run a different backend search must produce a different key,
 * or two distinct searches would share a search_id:
 *
 * - query + category scope: the search itself.
 * - correction mode: "Search instead for …" re-runs the SAME query with
 *   auto-correction off — a different backend query and result set.
 * - postcode: the search is location-scoped (nearby branches, stock,
 *   distances), so a postcode change re-runs it with different results. The
 *   module-level currentSearch survives the Search page remounting on that
 *   change, so without this the old location's id would be reused and the
 *   new search suppressed.
 */
function searchKey(
  query: string,
  categoryId: number | null,
  noCorrection: boolean,
  postcode: string | null | undefined,
): string {
  return [
    query,
    categoryId ?? '',
    noCorrection ? 'literal' : 'corrected',
    postcode ?? '',
  ].join('::')
}

/**
 * The search_id for the search identified by `key`.
 *
 * Reuses the current id when the key matches (pagination, a click, or coming
 * back from a product); otherwise this is a new search, so a fresh id is
 * minted and becomes current.
 */
function getOrMintSearchId(key: string): string {
  if (currentSearch?.key === key) {
    return currentSearch.id
  }
  const id = mintUuid()
  currentSearch = { key, id }
  return id
}


export function SearchPage() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  // Sort option type — matches the values the backend's ?sort= param accepts.
  type SortOption = 'relevance' | 'price' | 'distance'

  // Read current values from URL (source of truth)
  const urlQuery = searchParams.get('q') || ''
  // Priority: URL param → sessionStorage (persists across detail-page nav) → default.
  // 'relevance' keeps pure OpenSearch BM25 order; 'price' triggers the
  // backend bucket+price sort; 'distance' uses client-side sort after stock
  // checks settle (distance data comes from the stock-check responses).
  const urlSort: SortOption =
    (searchParams.get('sort') as SortOption) ||
    (sessionStorage.getItem('engineer_search_sort') as SortOption) ||
    'relevance'
  const urlPage = parseInt(searchParams.get('page') || '1', 10)
  // Optional category scope, set when the user picks a category suggestion.
  // null = normal keyword search. Parsed to a number for the API call.
  const urlCategoryParam = searchParams.get('category_id')
  const urlCategoryId = urlCategoryParam ? parseInt(urlCategoryParam, 10) : null
  const categoryIdForSearch =
    urlCategoryId !== null && Number.isFinite(urlCategoryId) ? urlCategoryId : null

  // Did-you-mean opt-out (Epic 3). Set to true when the user clicks the
  // "Search instead for …" revert link so the literal typed query is
  // searched without phrase-suggester auto-correction.
  const urlNoCorrection = searchParams.get('no_correction') === '1'

  // Local state for input field (for debouncing)
  const [searchInput, setSearchInput] = useState(urlQuery)
  // Held so the clear (X) button can hand focus back to the field.
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [session, setSession] = useState<EngineerSession | null>(null)
  // Current drilled category in the empty-state navigator. Page-level (not
  // inside CategoryBrowse) so the results "in {Category}" chip can send the user
  // back into the navigator at a parent level, mirroring the mock.
  const [browseCategoryId, setBrowseCategoryId] = useState<number | null>(null)
  // The category that drives scoping + the placeholder: an explicit search scope
  // (categoryIdForSearch) if set, otherwise the navigator's drilled position. So
  // typing while browsing Electrical searches within Electrical, matching the mock.
  const activeCategoryId = categoryIdForSearch ?? browseCategoryId
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const { addToast } = useToast()
  // Persisted in sessionStorage so back-navigation from the product detail page
  // restores the toggle state. The toggle becomes clickable once all stock
  // checks have settled (isAllPagesReady).
  const [showInStockOnly, setShowInStockOnly] = useState(
    () => sessionStorage.getItem('inStockFilter') === '1',
  )

  // Search-quality signal tracking. Both refs hold sets of "scope keys" that
  // identify a unique tracking event so we only fire each one once per
  // (query, page) combination — repeated clicks on the same result list or
  // re-navigations to the same page must not pollute the analytics table.
  // Cleared implicitly by component remount on full navigation away.
  const trackedFirstClickKeysRef = useRef<Set<string>>(new Set())
  const trackedPageTurnKeysRef = useRef<Set<string>>(new Set())
  // Prevents the in-stock reset effect from firing on the initial mount so
  // that back-navigation from the product detail page restores the toggle.
  const isInitialMountRef = useRef(true)

  // Debounced query - this is what triggers searches
  const [debouncedQuery, setDebouncedQuery] = useState(urlQuery)

  // Sync input field with URL when URL changes (e.g. back navigation)
  useEffect(() => {
    setSearchInput(urlQuery)
    setDebouncedQuery(urlQuery)
  }, [urlQuery])

  // Reset the in-stock toggle whenever a new search begins so the user
  // always sees raw backend results immediately.
  // Skipped on initial mount so that back-navigation from the product detail
  // page does not wipe the toggle state the user left behind.
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }
    setShowInStockOnly(false)
    sessionStorage.removeItem('inStockFilter')
  }, [debouncedQuery])

  // Load session on mount
  useEffect(() => {
    const savedSession = sessionStorage.getItem('engineerSession')
    if (savedSession) {
      setSession(JSON.parse(savedSession))
    } else {
      navigate(`/${tenantSlug}/engineer`)
    }
  }, [navigate, tenantSlug])

  // Check if postcode changed on another device (cross-device sync)
  usePostcodeSync(session)

  // Search is explicit (Enter key) — search-as-you-type was firing one
  // search + N stock-check fan-outs per keystroke, swamping supplier
  // APIs for "copper p", "copper pi", "copper pip", etc. before the
  // user finished typing. Engineers know what they want, so committing
  // only on Enter cuts request volume ~80% with no UX loss.
  // categoryId scopes the search to a category (set when a category
  // suggestion is selected); pass null for a normal keyword search, which
  // also clears any previous category scope.
  const commitSearch = (rawValue: string, categoryId: number | null = null) => {
    const trimmed = rawValue.trim()
    // Re-run when either the text OR the category scope changed. A plain
    // keyword commit with the same text and no category is a no-op.
    if (trimmed === debouncedQuery && categoryId === categoryIdForSearch) return
    setDebouncedQuery(trimmed)
    const newParams = new URLSearchParams(searchParams)
    if (trimmed) {
      newParams.set('q', trimmed)
    } else {
      newParams.delete('q')
    }
    if (categoryId !== null) {
      newParams.set('category_id', categoryId.toString())
    } else {
      newParams.delete('category_id')
    }
    newParams.delete('page')
    // Clear the Did-you-mean opt-out on every fresh search — the user
    // typing a new query implicitly opts back into auto-correction. The
    // revert button on the correction notice re-adds it when needed.
    newParams.delete('no_correction')
    setSearchParams(newParams, { replace: true })
  }

  const handleSearchSubmit = () => {
    // Keep the active category scope: typing a keyword while a category is
    // selected (or while browsing inside one) searches that keyword WITHIN the
    // category. To leave it, use the "in {Category}" widen chip.
    commitSearch(searchInput, activeCategoryId)
    searchInputRef.current?.blur()
  }

  // Clear (X) button inside the field. Always drops the keyword; whether the
  // page moves depends on the category:
  //  - A category is selected → stay in it. A category listing / category +
  //    keyword search re-lists the whole category (keyword removed); while
  //    only drilled into the category grid there are no results yet, so just
  //    the box empties.
  //  - No category → back to the search home page.
  // commitSearch is a no-op when nothing actually changes (e.g. typed but
  // never searched), so those cases only empty the box.
  const handleClearSearchInput = () => {
    setSearchInput('')
    setIsSuggestOpen(false)
    setActiveSuggestIndex(-1)
    if (categoryIdForSearch !== null) {
      commitSearch('', categoryIdForSearch)
    } else if (browseCategoryId === null) {
      commitSearch('', null)
    }
    // Keep the caret in the field so the phone keyboard stays open and the
    // engineer can retype straight away.
    searchInputRef.current?.focus()
  }

  // Suggestion dropdown state. Kept fully local — never touches the URL or
  // the main search query so the existing Enter-to-submit pipeline is
  // unchanged. Closes on commit, blur (mouse outside), or Escape.
  const [isSuggestOpen, setIsSuggestOpen] = useState(false)
  const [activeSuggestIndex, setActiveSuggestIndex] = useState(-1)
  const debouncedSuggestInput = useDebounce(searchInput, 250)

  const { data: suggestions = [], isFetching: isSuggestLoading } = useQuery({
    queryKey: ['search-suggestions', debouncedSuggestInput],
    queryFn: ({ signal }) => api.searchSuggestions(debouncedSuggestInput, signal),
    enabled: isSuggestOpen && debouncedSuggestInput.trim().length >= 2,
    staleTime: 60 * 1000,
  })

  // Group ordering for the dropdown: categories first, then product / mpn /
  // keyword suggestions under a separate heading. Stable within each group
  // so the backend's relevance order is preserved. Keyboard navigation
  // indexes into THIS array, so the dropdown must render the same order — pass
  // `orderedSuggestions` to both the nav helper and the dropdown.
  const orderedSuggestions = useMemo(() => {
    const products = suggestions.filter((item) => item.type !== 'category')
    const categories = suggestions.filter((item) => item.type === 'category')
    return [...categories, ...products]
  }, [suggestions])

  useEffect(() => {
    setActiveSuggestIndex(-1)
  }, [orderedSuggestions])

  // The dropdown prepends the user's literal typed query as the first
  // option (unless one of the suggestions is already an exact match)
  // so keyboard navigation needs to account for that extra row.
  const trimmedSearchInput = searchInput.trim()
  const showLiteralQueryOption = useMemo(() => {
    if (!trimmedSearchInput) return false
    // Only a NON-category exact match hides the literal "Search for X"
    // option, because product/mpn/keyword suggestions run the same broad
    // keyword search the literal option would. A category exact match runs
    // a DIFFERENT (category-scoped) search, so we keep the literal option
    // visible to preserve the broad-search choice.
    return !orderedSuggestions.some(
      (suggestion) =>
        suggestion.type !== 'category' &&
        suggestion.text.toLowerCase() === trimmedSearchInput.toLowerCase(),
    )
  }, [orderedSuggestions, trimmedSearchInput])
  const totalSuggestItems =
    (showLiteralQueryOption ? 1 : 0) + orderedSuggestions.length

  const getSuggestItemValue = (index: number): SearchSuggestion | null => {
    if (index < 0 || index >= totalSuggestItems) return null
    if (showLiteralQueryOption && index === 0) {
      return { text: trimmedSearchInput, type: 'keyword', categoryId: null }
    }
    const suggestionOffset = showLiteralQueryOption ? 1 : 0
    return orderedSuggestions[index - suggestionOffset] ?? null
  }

  const handleSuggestionSelect = (suggestion: SearchSuggestion) => {
    setSearchInput(suggestion.text)
    setIsSuggestOpen(false)
    setActiveSuggestIndex(-1)
    // A category suggestion sets the scope to THAT category (a dropdown category
    // wins over the currently-selected one). A product / MPN / literal-keyword
    // suggestion keeps the currently-selected category, so the keyword is
    // searched within it.
    const categoryId =
      suggestion.type === 'category' ? suggestion.categoryId : activeCategoryId
    commitSearch(suggestion.text, categoryId)
    searchInputRef.current?.blur()
  }

  // Tapping a category in the empty-state browser LISTS that category's
  // products — an empty-query browse scoped to the category (backend returns
  // the whole subtree). The search box is left empty (this is a browse, not a
  // keyword search); the active scope shows as a chip in the results header.
  const handleBrowseCategorySelect = (category: Category) => {
    setSearchInput('')
    commitSearch('', category.id)
  }

  // Categories (whole tree) used only to resolve the active browse scope's name
  // and parent for the results-header chip. Same hook the empty-state browser
  // uses (shared cache); only fetched when a category scope is actually active,
  // so a plain keyword search doesn't pull the whole tree.
  const { data: browseCategories = [] } = useCategories(
    !!session && activeCategoryId !== null,
  )
  // The active category (scope or browsed) — drives the search-box placeholder.
  const activeCategory =
    activeCategoryId !== null
      ? browseCategories.find((category) => category.id === activeCategoryId) ?? null
      : null
  // The chip shows only in a results state (an explicit search scope).
  const scopedCategory =
    categoryIdForSearch !== null
      ? browseCategories.find((category) => category.id === categoryIdForSearch) ?? null
      : null

  // Tapping the "in {Category}" pill's LABEL widens the scope by exactly one
  // level: a child → its parent, a top-level category → all (scope removed,
  // category grid shown again). Any keyword is kept, so a category+keyword
  // search widens too. (The pill's × is separate — see clearCategoryScope.)
  const widenScope = () => {
    const parentId = scopedCategory?.parentId ?? null
    // Keep the navigator's position in sync with where we're widening to.
    setBrowseCategoryId(parentId)
    if (debouncedQuery) {
      // Keyword present → widen the keyword search to the parent (keeps the
      // keyword; results stay), matching the mock. Restore the committed query
      // in the input too: q doesn't change, so the [urlQuery] sync effect won't
      // run, and an unsubmitted edit would otherwise linger in the field while
      // the results belong to the committed query.
      setSearchInput(debouncedQuery)
      commitSearch(debouncedQuery, parentId)
      return
    }
    // Category listing (no keyword) → drop back into the category navigator at
    // the parent level (Ln listing → L(n-1) sub-listing; L1 → initial state),
    // matching the mock's goUpCategory.
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('q')
    newParams.delete('category_id')
    newParams.delete('page')
    setSearchParams(newParams, { replace: true })
    setSearchInput('')
    setDebouncedQuery('')
  }

  // Remove the category scope entirely — the pill's × and the no-results
  // "Search all categories" both use this. Clears at ANY level and keeps any
  // keyword: with a keyword you stay on an unscoped product listing; without
  // one you return to the initial state (category grid). Also the escape from a
  // scope whose category can no longer be resolved after an RBAC change.
  const clearCategoryScope = () => {
    setBrowseCategoryId(null)
    if (debouncedQuery) {
      // Restore the committed query in the input (see widenScope): q doesn't
      // change here, so an unsubmitted edit would otherwise stay in the field
      // while the results are for the committed query.
      setSearchInput(debouncedQuery)
      commitSearch(debouncedQuery, null)
      return
    }
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('q')
    newParams.delete('category_id')
    newParams.delete('page')
    setSearchParams(newParams, { replace: true })
    setSearchInput('')
    setDebouncedQuery('')
  }

  const handleSearchInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (isSuggestOpen && activeSuggestIndex >= 0) {
        const value = getSuggestItemValue(activeSuggestIndex)
        if (value !== null) {
          handleSuggestionSelect(value)
          return
        }
      }
      setIsSuggestOpen(false)
      handleSearchSubmit()
      return
    }
    if (!isSuggestOpen || totalSuggestItems === 0) {
      if (event.key === 'Escape') {
        setIsSuggestOpen(false)
      }
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveSuggestIndex((prev) =>
        prev + 1 >= totalSuggestItems ? 0 : prev + 1,
      )
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveSuggestIndex((prev) =>
        prev <= 0 ? totalSuggestItems - 1 : prev - 1,
      )
    } else if (event.key === 'Escape') {
      setIsSuggestOpen(false)
      setActiveSuggestIndex(-1)
    }
  }

  // Handler to update page (updates URL)
  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams)
    if (newPage > 1) {
      newParams.set('page', newPage.toString())
    } else {
      newParams.delete('page')
    }
    setSearchParams(newParams, { replace: true })

    // Idea 1: capture every time a user navigates past page 1 of a search.
    // A user going to page 2+ is a passive "first page didn't satisfy" signal.
    // Fire once per (query, page) so a user paging back and forth doesn't
    // inflate the event count.
    const activeQuery = debouncedQuery.trim()
    if (activeQuery && newPage > 1) {
      const trackingKey = `${activeQuery}::${newPage}`
      if (!trackedPageTurnKeysRef.current.has(trackingKey)) {
        trackedPageTurnKeysRef.current.add(trackingKey)
        api
          .trackSearchPageTurn({ query: activeQuery, page_number: newPage })
          .catch(() => {
            // Analytics is fire-and-forget — never block the user on failure.
            trackedPageTurnKeysRef.current.delete(trackingKey)
          })
      }
    }
  }

  // Idea 3: capture the first product opened from a result list, with its
  // 1-indexed position. Frontend gates the call so only ONE record is
  // produced per (query, page) — subsequent clicks on the same list are
  // ignored. This gives the team a clean "search term → result depth" signal.
  const recordResultOpened = (product: MasterProduct, position: number) => {
    const activeQuery = debouncedQuery.trim()
    if (activeQuery) {
      const trackingKey = `${activeQuery}::${urlPage}`
      if (!trackedFirstClickKeysRef.current.has(trackingKey)) {
        trackedFirstClickKeysRef.current.add(trackingKey)
        const numericId = parseInt(product.id, 10)
        if (Number.isFinite(numericId)) {
          api
            .trackSearchProductClick({
              query: activeQuery,
              master_product_id: numericId,
              position,
              page_number: urlPage,
            })
            .catch(() => {
              // Analytics is fire-and-forget — never block navigation.
              trackedFirstClickKeysRef.current.delete(trackingKey)
            })
        }
      }
    }
    // PostHog: fire on EVERY click (the existing SearchQueryLog tracker above
    // records only the first per page). source tells the product page it came
    // from search.
    const clickedQuery = activeQuery || debouncedQuery.trim()
    trackSearchResultClicked({
      // Same key as search_performed, so this resolves to the id of the search
      // that produced these results — on any page of it.
      searchId: getOrMintSearchId(
        searchKey(
          clickedQuery,
          categoryIdForSearch,
          urlNoCorrection,
          session?.postcode,
        ),
      ),
      // String() defensively: MasterProduct.id is typed string but the API
      // sends a number, so passing it raw makes PostHog see the property as
      // both types ("mismatched"). product_viewed already stringifies.
      masterProductId: String(product.id),
      masterProductName: product.displayName,
      resultPosition: position,
      page: urlPage,
      query: clickedQuery,
    })
  }

  const handleProductCardClick = (product: MasterProduct, position: number) => {
    recordResultOpened(product, position)
    // DEV-50739 SP-003: a product a ticked branch can supply opens with that
    // branch pre-selected, so Add goes to the branch the engineer is
    // collecting from rather than the default nearest one.
    const productParams = new URLSearchParams()
    if (showInStockOnly) productParams.set('inStock', '1')
    // (Declared further down; only read on click, long after they exist.)
    const matchedBranchId = pickPrioritisedBranchId(
      activePriorityInfo.get(String(product.id)),
      liveRawPriceById,
    )
    if (matchedBranchId) productParams.set('branch', matchedBranchId)
    const productQuery = productParams.toString()
    navigate(
      `/${tenantSlug}/engineer/product/${product.id}${productQuery ? `?${productQuery}` : ''}`,
      { state: { source: ProductViewSource.SEARCH } },
    )
  }

  // QA-001 AC6: a linked card opens its supplier product page with the card's
  // branch and collection / delivery choice picked. The quantity and button
  // stay the page's own (quantity from the cart, Add / Update as usual).
  const handleLinkedCardClick = (
    product: MasterProduct,
    card: LinkedProductCardInfo,
    position: number,
    method: CartFulfillmentMethod,
  ) => {
    recordResultOpened(product, position)
    const productParams = new URLSearchParams({
      branchId: card.branchId,
      [QUICK_ADD_METHOD_PARAM]: method,
    })
    navigate(
      `/${tenantSlug}/engineer/supplier-product/${card.rawId}?${productParams.toString()}`,
      { state: { source: ProductViewSource.SEARCH } },
    )
  }

  // Idea 2: explicit feedback when the user can't find what they're looking
  // for. Parent owns the API call so it can surface a consistent error UX
  // through the modal. Throws on failure so the modal can show its inline
  // error message; resolves on success so the modal can close itself.
  // A success toast is fired here (not inside the modal) so the engineer
  // sees clear confirmation even after the modal has closed.
  const handleFeedbackSubmit = async (feedbackText: string) => {
    const activeQuery = debouncedQuery.trim()
    await api.submitSearchFeedback({
      query: activeQuery,
      feedback_text: feedbackText,
    })
    addToast('Thanks — we got your feedback', 'success', 3000)
  }

  // Handler to change sort option. Persists to both URL and sessionStorage so
  // the choice survives back-navigation (URL) and remount after detail-page nav
  // (sessionStorage). Always resets to page 1.
  const handleSortChange = (sort: SortOption) => {
    sessionStorage.setItem('engineer_search_sort', sort)
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sort', sort)
    newParams.delete('page')
    setSearchParams(newParams, { replace: true })
  }

  // Fetch recent purchases (disabled - backend endpoint not implemented)
  const recentPurchases: RecentPurchase[] = []

  // Search products. Stock-aware demotion is no longer requested from the
  // backend — the search returns immediately in OpenSearch relevance order,
  // and per-card stock pills are hydrated below via per-supplier requests.
  const { data: searchData, isFetching: isSearching } = useQuery({
    queryKey: [
      'search-master-products',
      debouncedQuery,
      urlPage,
      session?.postcode,
      session?.latitude,
      session?.longitude,
      categoryIdForSearch,
      urlNoCorrection,
    ],
    queryFn: () =>
      api.searchMasterProducts(
        debouncedQuery,
        'relevance',
        urlPage,
        session?.postcode,
        session?.latitude,
        session?.longitude,
        categoryIdForSearch,
        urlNoCorrection,
      ),
    // Fire for a keyword search OR a category-only browse (empty q + a
    // category_id lists that category). Keyword-only behaviour is unchanged.
    enabled:
      !!session &&
      (debouncedQuery.trim().length > 0 || categoryIdForSearch !== null),
  })

  // Memoize array fallbacks so the empty `[]` doesn't create new references
  // each render — React Query's `data` is stable across renders, but the
  // `?? []` fallback below it isn't, which would otherwise re-run every
  // downstream useMemo/useQueries dependency check.
  const results = useMemo(() => searchData?.products ?? [], [searchData?.products])
  const totalProducts = searchData?.totalProducts ?? 0
  const hasNextPage = searchData?.hasNextPage ?? false
  const hasPrevPage = searchData?.hasPrevPage ?? false

  // ─── Branch prioritisation (DEV-50739 SP-003) ─────────────────────────────
  // With one or more "Show first" pills ticked, products a ticked branch can
  // supply move to the top of whatever ordering is in force — the list is
  // split, never re-sorted, so each group keeps the existing relevance /
  // price / distance order. In-stock matches lead, backorder matches follow.
  // Nothing is hidden and no extra requests are made: the branch lists ride
  // on the stock-check responses already fetched for the pills.
  const { selectedBranches } = useBranchPriority()
  const prioritisedBranchIds = useMemo(
    () => new Set(selectedBranches.map((branch) => branch.branchId)),
    [selectedBranches],
  )
  // Priority is applied whenever one or more pills are ticked. (There is no
  // "explore" mode: the SP-004 no-match card is just a message, and the full
  // list in standard order is always right below it.)
  const hasBranchPriority = prioritisedBranchIds.size > 0

  // Analytics: report search_performed once the search has settled. Keyed on
  // query + category (not page), so it fires once per distinct search and not
  // on pagination. Must fire on zero results — that is the catalogue-gap
  // signal — so it is not gated on totalProducts. Only an empty/whitespace
  // query is suppressed. The supplier / branch filters are the ticked "Show
  // first" branches in force for this search (none while exploring):
  // distinct supplier names, and "Supplier · Branch" labels; the category
  // name, when a category suggestion scopes the search, is the committed
  // query itself (the app sets query = category name and clears the scope
  // on any keystroke).
  useEffect(() => {
    if (isSearching || !searchData) return
    const activeQuery = debouncedQuery.trim()
    if (!activeQuery) return
    // Resolve (or mint) this search's id, then report it once. De-duplicating
    // by id rather than by keyword means a genuine repeat of the same keyword
    // (a different search) is counted, while pagination and the product
    // round-trip (same id) are not.
    const searchId = getOrMintSearchId(
      searchKey(
        activeQuery,
        categoryIdForSearch,
        urlNoCorrection,
        session?.postcode,
      ),
    )
    if (reportedSearchIds.has(searchId)) return
    reportedSearchIds.add(searchId)

    const categoryActive = categoryIdForSearch !== null
    const appliedBranches = hasBranchPriority ? selectedBranches : []
    const appliedSuppliers = [
      ...new Set(appliedBranches.map((branch) => branch.supplierName)),
    ]
    trackSearchPerformed({
      searchId,
      query: activeQuery,
      resultsCount: totalProducts,
      supplierFilters: appliedSuppliers.length > 0 ? appliedSuppliers.join(', ') : null,
      supplierFilterCount: appliedSuppliers.length,
      branchFilters:
        appliedBranches.length > 0
          ? appliedBranches
              .map((branch) => `${branch.supplierName} · ${branch.branchName}`)
              .join(', ')
          : null,
      branchFilterCount: appliedBranches.length,
      categoryFilters: categoryActive ? activeQuery : null,
      categoryFilterCount: categoryActive ? 1 : 0,
    })
  }, [
    isSearching,
    searchData,
    debouncedQuery,
    categoryIdForSearch,
    urlNoCorrection,
    session?.postcode,
    totalProducts,
    hasBranchPriority,
    selectedBranches,
  ])
  // Synonym / Did-you-mean resolution outcome (Epic 3). `wasCorrected` is
  // true only when the phrase suggester replaced the literal query with a
  // better-spelled alternative. The CorrectionNotice below the search bar
  // renders only in that case.
  const searchQueryInfo = searchData?.searchQuery
  // Per-supplier master ID lists from the backend. Keys are supplier IDs,
  // values are master IDs that supplier carries. Used both to:
  //  - know which suppliers to fan out to (Object.keys)
  //  - send only relevant IDs in each stock-check request
  //  - know which suppliers can legitimately vote on each master (the
  //    stitcher below ignores pending state from suppliers that don't
  //    carry the master, so an unrelated slow supplier won't keep pills
  //    in "Checking…" forever)
  const masterIdsBySupplier = useMemo(
    () => searchData?.masterIdsBySupplier ?? {},
    [searchData?.masterIdsBySupplier],
  )

  // Stable supplier list sorted for deterministic useQueries config order.
  const windowSupplierIds = useMemo(
    () =>
      Object.keys(masterIdsBySupplier)
        .map((key) => parseInt(key, 10))
        .filter((value) => Number.isFinite(value))
        .sort((a, b) => a - b),
    [masterIdsBySupplier],
  )

  // Reverse map: master_id → Set<supplier_id> for O(1) lookup in the
  // stitcher. Built once per render of stockStateByMaster (cheap; 500
  // masters × ~2-3 suppliers each).
  const suppliersByMaster = useMemo(() => {
    const map = new Map<string, Set<number>>()
    for (const supplierKey of Object.keys(masterIdsBySupplier)) {
      const supplierId = parseInt(supplierKey, 10)
      if (!Number.isFinite(supplierId)) continue
      for (const masterId of masterIdsBySupplier[supplierId] ?? []) {
        let set = map.get(masterId)
        if (!set) {
          set = new Set<number>()
          map.set(masterId, set)
        }
        set.add(supplierId)
      }
    }
    return map
  }, [masterIdsBySupplier])

  // Progressive stock-pill resolver. One useQuery per supplier in the
  // search window — each call carries ONLY the masters that supplier
  // sells, so request bodies stay small and the response is just real
  // in/out signal (no_listing rarely shows up). Slow suppliers don't
  // block fast ones; each query resolves independently.
  const stockQueries = useQueries({
    queries: windowSupplierIds.map((supplierId) => {
      const supplierMasterIds = masterIdsBySupplier[supplierId] ?? []
      return {
        queryKey: [
          'stock-check',
          debouncedQuery,
          session?.postcode,
          supplierId,
          supplierMasterIds.join(','),
        ],
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          // Hard frontend timeout — caller's AbortController fires after
          // STOCK_CHECK_SUPPLIER_TIMEOUT_MS so a hung supplier resolves
          // its pill as an error (doesn't vote) rather than stuck-checking.
          const timeoutController = new AbortController()
          const timeoutId = window.setTimeout(
            () => timeoutController.abort(),
            STOCK_CHECK_SUPPLIER_TIMEOUT_MS,
          )
          const onParentAbort = () => timeoutController.abort()
          signal.addEventListener('abort', onParentAbort)
          try {
            return await api.checkSupplierStock(
              supplierId,
              supplierMasterIds,
              session!.postcode,
              session?.latitude,
              session?.longitude,
              timeoutController.signal,
              debouncedQuery,
            )
          } finally {
            window.clearTimeout(timeoutId)
            signal.removeEventListener('abort', onParentAbort)
          }
        },
        enabled:
          !!session?.postcode &&
          supplierMasterIds.length > 0,
        staleTime: STOCK_CHECK_STALE_MS,
        // No retries: a cancelled/timed-out supplier call stays cancelled.
        // The previous retry-after-2s caused a visible flicker — the count
        // would settle, then disappear again when the retry started, then
        // settle again when it completed. We honour the first outcome of
        // each supplier call and let the user refresh if they want fresher
        // data; cancelled suppliers simply don't vote on stock state.
        retry: 0,
      }
    }),
  })

  // Progressive per-supplier live-price refresh. Fires in parallel with
  // (and independent of) the stock-check fan-out above. Each call hits
  // the backend's stored-first pricing pipeline: valid stored prices are
  // served from DB; expired / missing prices trigger a live supplier API
  // call whose result is written back to RawProductPrice. Cards keep
  // showing the search-response `lowestPrice` until every relevant
  // supplier has responded for that master, then swap to the merged live
  // min (see `livePriceByMaster` below) — prevents a shown price from
  // flickering upward as slower suppliers resolve.
  const livePriceQueries = useQueries({
    queries: windowSupplierIds.map((supplierId) => {
      const supplierMasterIds = masterIdsBySupplier[supplierId] ?? []
      return {
        queryKey: [
          'refresh-prices',
          debouncedQuery,
          supplierId,
          supplierMasterIds.join(','),
        ],
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          const timeoutController = new AbortController()
          const timeoutId = window.setTimeout(
            () => timeoutController.abort(),
            STOCK_CHECK_SUPPLIER_TIMEOUT_MS,
          )
          const onParentAbort = () => timeoutController.abort()
          signal.addEventListener('abort', onParentAbort)
          try {
            return await api.refreshSupplierPrices(
              supplierId,
              supplierMasterIds,
              timeoutController.signal,
            )
          } finally {
            window.clearTimeout(timeoutId)
            signal.removeEventListener('abort', onParentAbort)
          }
        },
        enabled: supplierMasterIds.length > 0,
        staleTime: STOCK_CHECK_STALE_MS,
        retry: 0,
      }
    }),
  })

  // Stitch per-master pill state from accumulated supplier responses.
  //
  // The backend ships `master_ids_by_supplier`, so for each master we
  // know exactly which suppliers can vote. A slow / unrelated supplier
  // no longer keeps "Checking…" pills on products it doesn't sell.
  //
  // Pill rule:
  //   - any *relevant* supplier reports in_stock    → 'in_stock'   (short-circuit)
  //   - some *relevant* supplier still pending      → 'checking'
  //   - all *relevant* responses in, ≥1 out_of_stock → 'out_of_stock'
  //   - all *relevant* responses in, all errored    → 'unknown'    (no pill)
  //
  // Map supplierId → query result for O(1) lookup. windowSupplierIds and
  // stockQueries are guaranteed same length / same order by useQueries.
  const stockStateByMaster = useMemo(() => {
    const map = new Map<string, SearchCardStockState>()
    if (results.length === 0) return map

    const queryBySupplier = new Map<number, (typeof stockQueries)[number]>()
    windowSupplierIds.forEach((supplierId, idx) => {
      queryBySupplier.set(supplierId, stockQueries[idx])
    })

    for (const product of results) {
      const masterKey = String(product.id)
      // Backend flagged this master as reachable only via out-of-range
      // suppliers — show the "No nearby branch" pill directly. No stock call
      // is fired for it (it's absent from master_ids_by_supplier), so short-
      // circuit before the supplier stitching below.
      if (product.hasNearbyBranch === false) {
        map.set(product.id, 'no_branch')
        continue
      }
      const relevantSuppliers = suppliersByMaster.get(masterKey)
      // No supplier carries this master (shouldn't happen post-filter,
      // but be defensive) — leave it unknown so no pill renders.
      if (!relevantSuppliers || relevantSuppliers.size === 0) {
        map.set(product.id, 'unknown')
        continue
      }

      let hasInStock = false
      let hasOutOfStock = false
      let hasBackorder = false
      let anyPending = false

      for (const supplierId of relevantSuppliers) {
        const query = queryBySupplier.get(supplierId)
        if (!query) continue // supplier in map but no query yet — treat as silent

        const isPending =
          query.isPending || (query.isFetching && !query.data && !query.isError)
        if (isPending) {
          anyPending = true
          continue
        }
        if (query.isError || !query.data) {
          // Transport-level failure — doesn't vote.
          continue
        }
        const masterResults = query.data.masterResults
        if (!masterResults || typeof masterResults !== 'object') {
          continue
        }
        const status = masterResults[masterKey]
        if (status === 'in_stock') {
          hasInStock = true
          break
        }
        // 'backorder' = no stock but this supplier can backorder → amber
        // pill unless another supplier turns out to have stock (green wins).
        if (status === 'backorder') {
          hasBackorder = true
        } else if (status === 'out_of_stock') {
          hasOutOfStock = true
        }
        // 'error', 'no_listing', undefined → doesn't vote.
      }

      if (hasInStock) {
        map.set(product.id, 'in_stock')
      } else if (anyPending) {
        map.set(product.id, 'checking')
      } else if (hasBackorder) {
        map.set(product.id, 'backorder')
      } else if (hasOutOfStock) {
        map.set(product.id, 'out_of_stock')
      } else {
        // Reached only when every relevant supplier responded but none
        // gave a positive or negative vote — i.e., they all errored or
        // returned no_listing. Showing nothing here is worse UX than
        // showing OOS: the engineer has no way to act, and the detail
        // page (which runs a fresh per-supplier live-stock call) often
        // recovers from transient failures anyway. So we surface OOS
        // and let the engineer click through if they really need the
        // product. The `unknown` state is now effectively only used
        // for products with no relevant supplier at all (handled above).
        map.set(product.id, 'out_of_stock')
      }
    }
    return map
  }, [results, windowSupplierIds, stockQueries, suppliersByMaster])

  // Merged live-price map. Only exposes a master once every relevant
  // supplier has responded, so the shown price never flickers upward as
  // slower suppliers resolve. Value carries three pieces the render layer
  // needs to keep price + supplier attribution consistent:
  //   - lowestPrice: min live/stored price across the responding suppliers
  //   - lowestPriceSupplierName: the supplier that owns that min (so we
  //     don't render the new number beside the old supplier's name when
  //     the winner shifts during hydration)
  //   - pricesBySupplierName: each responding supplier's live price for
  //     this master, used to patch the in-stock supplier list so every
  //     entry (not just the aggregate min) reflects the refreshed number
  // Suppliers that returned null for a master (no listing / all raws
  // unavailable) don't contribute.
  interface LivePriceInfo {
    // `null` means the refresh has settled but no supplier has a live
    // price for this master (all raws returned unavailable / soft-deleted).
    // Render clears the card to a hyphen so the stale stored `lowestPrice`
    // from the initial search response doesn't linger.
    lowestPrice: number | null
    lowestPriceSupplierName: string
    // Per-supplier live price for this master. Values:
    //   number → supplier returned a valid live price
    //   null   → supplier responded but has no live price (all raws
    //            unavailable or soft-deleted). Hydration uses this to
    //            clear the stale in-stock entry price, so a supplier
    //            whose stock check said in_stock but whose raws are
    //            now unavailable doesn't keep advertising the old
    //            stored number.
    //   absent → supplier didn't respond for this master (not relevant
    //            or query failed) — hydration leaves the entry alone.
    pricesBySupplierName: Map<string, number | null>
  }
  const livePriceByMaster = useMemo(() => {
    const map = new Map<string, LivePriceInfo>()
    if (suppliersByMaster.size === 0) return map

    const queryBySupplier = new Map<
      number,
      (typeof livePriceQueries)[number]
    >()
    windowSupplierIds.forEach((supplierId, idx) => {
      queryBySupplier.set(supplierId, livePriceQueries[idx])
    })

    // Iterate the full cached window (up to ~100 masters) rather than just
    // the current backend page. Client-side pagination modes (price sort,
    // in-stock filter, all-pages distance sort) render cards from other
    // backend pages; scoping to `results` would leave those unhydrated
    // even though their supplier prices were already fetched.
    for (const masterKey of suppliersByMaster.keys()) {
      const relevantSuppliers = suppliersByMaster.get(masterKey)
      if (!relevantSuppliers || relevantSuppliers.size === 0) continue

      let allSucceeded = true
      let mergedMin: number | null = null
      let mergedSupplierName = ''
      const perSupplier = new Map<string, number | null>()
      for (const supplierId of relevantSuppliers) {
        const query = queryBySupplier.get(supplierId)
        // Only SUCCESSFUL responses count toward a merged live result.
        // A supplier that errored or is still pending would produce a
        // partial picture — publishing the min across the survivors can
        // replace a valid stored min with a higher live price, or clear
        // an OOS card to `-` when every request failed. In both cases
        // we'd rather keep the stored `lowestPrice` authoritative.
        if (!query || !query.isSuccess) {
          allSucceeded = false
          break
        }
        const price = query.data?.masterPrices?.[masterKey]
        const supplierName = query.data?.supplierName
        if (!supplierName) continue
        // Record every responded supplier — even those returning a null
        // price — so hydration can clear stale in-stock entries whose
        // raws became unavailable between stock check and price refresh.
        perSupplier.set(supplierName, price ?? null)
        if (price != null && (mergedMin === null || price < mergedMin)) {
          mergedMin = price
          mergedSupplierName = supplierName
        }
      }
      if (!allSucceeded) continue
      // Always publish an entry once every relevant supplier has settled,
      // even when no supplier returned a price. That lets the render
      // clear an obsolete stored value instead of leaving a soft-deleted
      // listing's price on the card.
      map.set(masterKey, {
        lowestPrice: mergedMin,
        lowestPriceSupplierName: mergedSupplierName,
        pricesBySupplierName: perSupplier,
      })
    }
    return map
  }, [windowSupplierIds, livePriceQueries, suppliersByMaster])

  // Sort key that mirrors what the card actually displays: hydrated
  // number when the refresh has settled, stored `lowestPrice` otherwise.
  // Aligning sort with display prevents the "£30 above £22" issue where
  // the ordering was fixed at stored prices while the cards showed
  // hydrated ones. Settled-unavailable (live returned no price) pins to
  // the bottom via Infinity so a soft-deleted listing doesn't hold a
  // top slot with a stale number.
  const priceForSort = useCallback(
    (product: MasterProduct): number => {
      const live = livePriceByMaster.get(String(product.id))
      if (live !== undefined) return live.lowestPrice ?? Infinity
      return product.lowestPrice || Infinity
    },
    [livePriceByMaster],
  )

  // Per-master "still refreshing price" flag. True while at least one
  // relevant supplier's live-price call is still in flight for the
  // master. Drives the card's `CHECKING PRICE…` pill, which suppresses
  // the definitive `-` for masters that have no stored price yet AND
  // are waiting on a live response. Once every relevant supplier settles,
  // the flag drops and the card renders the real price or a final `-`.
  const priceRefreshingByMaster = useMemo(() => {
    const map = new Map<string, boolean>()
    if (suppliersByMaster.size === 0) return map

    const queryBySupplier = new Map<
      number,
      (typeof livePriceQueries)[number]
    >()
    windowSupplierIds.forEach((supplierId, idx) => {
      queryBySupplier.set(supplierId, livePriceQueries[idx])
    })

    // Same reasoning as `livePriceByMaster` — iterate the full window so
    // products rendered by client-side pagination modes get the pill
    // instead of a stale hyphen.
    for (const masterKey of suppliersByMaster.keys()) {
      const relevantSuppliers = suppliersByMaster.get(masterKey)
      if (!relevantSuppliers || relevantSuppliers.size === 0) continue
      for (const supplierId of relevantSuppliers) {
        const query = queryBySupplier.get(supplierId)
        const settled =
          query && (query.isSuccess || query.isError || query.status === 'error')
        if (!settled) {
          map.set(masterKey, true)
          break
        }
      }
    }
    return map
  }, [windowSupplierIds, livePriceQueries, suppliersByMaster])

  // Toggle is enabled once the current page's stock checks have all settled.
  // Distance to the nearest relevant branch per master, stitched from the
  // per-supplier stock-check responses. Each response now carries
  // master_distances: { [masterId]: miles | null }. We take the minimum
  // across all suppliers so a product at a closer Wolseley branch beats a
  // further CPS branch, matching how a user would think about proximity.
  const nearestDistanceByMaster = useMemo(() => {
    const map = new Map<string, number>()
    for (const query of stockQueries) {
      if (!query.data?.masterDistances) continue
      for (const [masterId, dist] of Object.entries(query.data.masterDistances)) {
        if (dist == null) continue
        const existing = map.get(masterId)
        if (existing === undefined || dist < existing) {
          map.set(masterId, dist)
        }
      }
    }
    return map
  }, [stockQueries])

  // Per-master list of suppliers that voted in_stock, with their branch
  // distance and the stored selling price (BM-001). Sorted by price
  // ascending — Noreen: "show whichever one has stock available, either
  // in branch or in warehouse", so the cheapest in-stock supplier wins the
  // primary card slot. Distance is the tie-breaker (nearest wins) so a
  // £10 supplier 2mi away beats a £10 supplier 12mi away. Entries with
  // an unknown price sort to the end so they never overshadow real prices.
  const inStockSuppliersByMaster = useMemo(() => {
    const map = new Map<
      string,
      Array<{ name: string; distanceMi: number | null; price: number | null }>
    >()
    // Suppliers that can backorder the master (no stock nearby). Used for
    // the headline ONLY when no supplier has stock, so a BACKORDER card
    // names a supplier that can actually supply it rather than the stored
    // cheapest listing (which may be unavailable). In-stock cards unchanged.
    const backorderMap = new Map<
      string,
      Array<{ name: string; distanceMi: number | null; price: number | null }>
    >()
    windowSupplierIds.forEach((_supplierId, idx) => {
      const query = stockQueries[idx]
      if (!query?.data) return
      const supplierName = query.data.supplierName
      if (!supplierName) return
      for (const [masterId, statusValue] of Object.entries(query.data.masterResults ?? {})) {
        if (statusValue !== 'in_stock' && statusValue !== 'backorder') continue
        const target = statusValue === 'in_stock' ? map : backorderMap
        const list = target.get(masterId) ?? []
        list.push({
          name: supplierName,
          distanceMi: query.data.masterDistances?.[masterId] ?? null,
          price: query.data.masterPrices?.[masterId] ?? null,
        })
        target.set(masterId, list)
      }
    })
    for (const [masterId, list] of backorderMap.entries()) {
      if (!map.has(masterId)) map.set(masterId, list)
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const pa = a.price ?? Infinity
        const pb = b.price ?? Infinity
        if (pa !== pb) return pa - pb
        return (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity)
      })
    }
    return map
  }, [windowSupplierIds, stockQueries])

  // Stock data is already in cache from the normal pill resolution above —
  // no new API calls needed when the toggle is clicked.
  const isStockCheckComplete = useMemo(() => {
    if (results.length === 0) return false
    if (windowSupplierIds.length === 0) return true
    return !results.some((p) => (stockStateByMaster.get(p.id) ?? 'checking') === 'checking')
  }, [results, windowSupplierIds, stockStateByMaster])

  // Current-page results with optional in-stock filter and distance sort.
  // Distance sort is client-side using nearestDistanceByMaster (populated by
  // stock-check responses). Relevance and price are sorted server-side already.
  const displayResults = useMemo(() => {
    let filtered = showInStockOnly
      ? results.filter((p) => stockStateByMaster.get(p.id) === 'in_stock')
      : results
    if (urlSort === 'price') {
      return [...filtered].sort((a, b) => {
        const far = farBranchLast(a, b)
        if (far !== 0) return far
        return priceForSort(a) - priceForSort(b)
      })
    }
    if (urlSort === 'distance' && nearestDistanceByMaster.size > 0) {
      return [...filtered].sort((a, b) => {
        const far = farBranchLast(a, b)
        if (far !== 0) return far
        const da = nearestDistanceByMaster.get(String(a.id)) ?? Infinity
        const db = nearestDistanceByMaster.get(String(b.id)) ?? Infinity
        return da - db
      })
    }
    return filtered
  }, [results, showInStockOnly, stockStateByMaster, urlSort, nearestDistanceByMaster, priceForSort])

  // ─── Background prefetch: full in-stock count + correct pagination ────────
  // Triggered once isStockCheckComplete. Current page's queries are cache hits.
  // Other pages fire fresh but the backend stock cache is warm → near-instant.

  const totalPagesCount = useMemo(
    () => Math.max(1, Math.ceil(totalProducts / 20)),
    [totalProducts],
  )

  const allPageNums = useMemo(
    () =>
      isStockCheckComplete && totalPagesCount > 0
        ? Array.from({ length: totalPagesCount }, (_, i) => i + 1)
        : [],
    [isStockCheckComplete, totalPagesCount],
  )

  const allPageSearchQueries = useQueries({
    queries: allPageNums.map((pageNum) => ({
      queryKey: [
        'search-master-products',
        debouncedQuery,
        pageNum,
        session?.postcode,
        session?.latitude,
        session?.longitude,
        categoryIdForSearch,
        // Include urlNoCorrection so these background pages share cache
        // entries with the main page query and use the same correction state.
        urlNoCorrection,
      ],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.searchMasterProducts(
          debouncedQuery,
          'relevance',
          pageNum,
          session?.postcode,
          session?.latitude,
          session?.longitude,
          categoryIdForSearch,
          urlNoCorrection, // must match main query correction state (arg 8)
          signal,          // AbortSignal now correctly at arg 9
        ),
      enabled: isStockCheckComplete,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    })),
  })

  const allPagesFetched = useMemo(
    () =>
      allPageNums.length > 0 &&
      allPageSearchQueries.every((q) => q.isSuccess),
    [allPageNums, allPageSearchQueries],
  )

  // Per-supplier stock config for ALL pages. Same key shape as per-page
  // queries → current page entries are cache hits, other pages fire fresh.
  const allPagesStockQueryList = useMemo(() => {
    if (!allPagesFetched) return []
    const entries: Array<{ supplierId: number; masterIds: string[] }> = []
    for (let i = 0; i < allPageNums.length; i++) {
      const pageSupplierMap =
        allPageNums[i] === urlPage
          ? (masterIdsBySupplier as Record<string, string[]>)
          : ((allPageSearchQueries[i]?.data?.masterIdsBySupplier as Record<string, string[]>) ?? {})
      for (const [supplierKey, masterIds] of Object.entries(pageSupplierMap)) {
        const supplierId = parseInt(supplierKey, 10)
        if (!Number.isFinite(supplierId) || (masterIds as string[]).length === 0) continue
        entries.push({ supplierId, masterIds: masterIds as string[] })
      }
    }
    return entries
  }, [allPagesFetched, allPageNums, urlPage, masterIdsBySupplier, allPageSearchQueries])

  const allPagesStockQueries = useQueries({
    queries: allPagesStockQueryList.map(({ supplierId, masterIds }) => ({
      queryKey: [
        'stock-check',
        debouncedQuery,
        session?.postcode,
        supplierId,
        masterIds.join(','),
      ],
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const timeoutController = new AbortController()
        const timeoutId = window.setTimeout(
          () => timeoutController.abort(),
          STOCK_CHECK_SUPPLIER_TIMEOUT_MS,
        )
        const onParentAbort = () => timeoutController.abort()
        signal.addEventListener('abort', onParentAbort)
        try {
          return await api.checkSupplierStock(
            supplierId,
            masterIds,
            session!.postcode,
            session?.latitude,
            session?.longitude,
            timeoutController.signal,
            debouncedQuery,
          )
        } finally {
          window.clearTimeout(timeoutId)
          signal.removeEventListener('abort', onParentAbort)
        }
      },
      enabled: allPagesFetched && !!session?.postcode && masterIds.length > 0,
      staleTime: STOCK_CHECK_STALE_MS,
      // No retries — see stockQueries above for rationale. Keeps
      // isAllPagesReady from oscillating during background refetches.
      retry: 0,
    })),
  })

  // Sticky-readiness: use q.isFetched (true once a query has settled at least
  // once, stays true through refetches) instead of (isSuccess || isError)
  // which flips back to false while react-query retries a failed/cancelled
  // call. Without stickiness, a single supplier retry would briefly knock
  // isAllPagesReady back to false, blanking the count and re-disabling the
  // toggle every 2 seconds until the retry settles.
  const isAllPagesReady = useMemo(
    () =>
      allPagesFetched &&
      (allPagesStockQueryList.length === 0 ||
        allPagesStockQueries.every((q) => q.isFetched)),
    [allPagesFetched, allPagesStockQueryList, allPagesStockQueries],
  )

  // When the full in-stock dataset becomes available, reset to page 1 so the
  // client-side in-stock pagination starts from a valid index.
  useEffect(() => {
    if (!isAllPagesReady) return
    if (!showInStockOnly) return
    const currentPage = parseInt(searchParams.get('page') || '1', 10)
    if (currentPage > 1) {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('page')
      setSearchParams(newParams, { replace: true })
    }
  }, [isAllPagesReady, showInStockOnly]) // eslint-disable-line react-hooks/exhaustive-deps

  // All products across all pages in page order
  const allProductsFlat = useMemo(() => {
    if (!allPagesFetched) return []
    return allPageNums.flatMap((pageNum, i) =>
      pageNum === urlPage
        ? results
        : ((allPageSearchQueries[i]?.data?.products as MasterProduct[]) ?? []),
    )
  }, [allPagesFetched, allPageNums, urlPage, results, allPageSearchQueries])

  // Supplier-set per master across all pages (for stitcher)
  const allPagesSuppliersByMaster = useMemo(() => {
    if (!allPagesFetched) return new Map<string, Set<number>>()
    const map = new Map<string, Set<number>>()
    for (let i = 0; i < allPageNums.length; i++) {
      const pageSupplierMap =
        allPageNums[i] === urlPage
          ? (masterIdsBySupplier as Record<string, string[]>)
          : ((allPageSearchQueries[i]?.data?.masterIdsBySupplier as Record<string, string[]>) ?? {})
      for (const [supplierKey, masterIds] of Object.entries(pageSupplierMap)) {
        const supplierId = parseInt(supplierKey, 10)
        if (!Number.isFinite(supplierId)) continue
        for (const masterId of masterIds as string[]) {
          let set = map.get(masterId)
          if (!set) { set = new Set<number>(); map.set(masterId, set) }
          set.add(supplierId)
        }
      }
    }
    return map
  }, [allPagesFetched, allPageNums, urlPage, masterIdsBySupplier, allPageSearchQueries])

  // Flat lookup: `${supplierId}__${masterId}` → raw status string
  const allPagesStockStatusMap = useMemo(() => {
    const map = new Map<string, string>()
    for (let i = 0; i < allPagesStockQueryList.length; i++) {
      const { supplierId, masterIds } = allPagesStockQueryList[i]
      const masterResults = allPagesStockQueries[i]?.data?.masterResults as
        | Record<string, string>
        | undefined
      if (!masterResults) continue
      for (const masterId of masterIds) {
        const status = masterResults[masterId]
        if (status) map.set(`${supplierId}__${masterId}`, status)
      }
    }
    return map
  }, [allPagesStockQueryList, allPagesStockQueries])

  // Stock state for every product across all pages
  const allPagesStockStateByMaster = useMemo(() => {
    if (!isAllPagesReady || allProductsFlat.length === 0) return stockStateByMaster
    const map = new Map<string, SearchCardStockState>()
    for (const product of allProductsFlat) {
      const masterKey = String(product.id)
      if (product.hasNearbyBranch === false) {
        map.set(product.id, 'no_branch')
        continue
      }
      const relevantSuppliers = allPagesSuppliersByMaster.get(masterKey)
      if (!relevantSuppliers || relevantSuppliers.size === 0) {
        map.set(product.id, 'unknown')
        continue
      }
      let hasInStock = false
      let hasBackorder = false
      for (const supplierId of relevantSuppliers) {
        const status = allPagesStockStatusMap.get(`${supplierId}__${masterKey}`)
        if (status === 'in_stock') { hasInStock = true; break }
        if (status === 'backorder') { hasBackorder = true }
      }
      map.set(product.id, hasInStock ? 'in_stock' : hasBackorder ? 'backorder' : 'out_of_stock')
    }
    return map
  }, [isAllPagesReady, allProductsFlat, allPagesSuppliersByMaster, allPagesStockStatusMap, stockStateByMaster])

  // Distance map for all pages — same logic as nearestDistanceByMaster but
  // sourced from allPagesStockQueries which covers every page's products.
  const allPagesNearestDistanceByMaster = useMemo(() => {
    const map = new Map<string, number>()
    for (const query of allPagesStockQueries) {
      if (!query.data?.masterDistances) continue
      for (const [masterId, dist] of Object.entries(query.data.masterDistances)) {
        if (dist == null) continue
        const existing = map.get(masterId)
        if (existing === undefined || dist < existing) {
          map.set(masterId, dist)
        }
      }
    }
    return map
  }, [allPagesStockQueries])

  // All-pages equivalent of inStockSuppliersByMaster: per-master list of
  // suppliers that voted in_stock across every fetched page, sorted by
  // price ascending (nearest branch tie-breaker). Used by the card when
  // the toggle / distance-sort background prefetch has settled.
  const allPagesInStockSuppliersByMaster = useMemo(() => {
    const map = new Map<
      string,
      Array<{ name: string; distanceMi: number | null; price: number | null }>
    >()
    // Backorder-capable suppliers, used only for masters with no in-stock
    // supplier (see inStockSuppliersByMaster).
    const backorderMap = new Map<
      string,
      Array<{ name: string; distanceMi: number | null; price: number | null }>
    >()
    for (const query of allPagesStockQueries) {
      if (!query.data) continue
      const supplierName = query.data.supplierName
      if (!supplierName) continue
      for (const [masterId, statusValue] of Object.entries(query.data.masterResults ?? {})) {
        if (statusValue !== 'in_stock' && statusValue !== 'backorder') continue
        const target = statusValue === 'in_stock' ? map : backorderMap
        const list = target.get(masterId) ?? []
        // Guard against duplicates when the same (supplier, master) pair
        // appears across multiple page-scoped queries.
        if (list.some((entry) => entry.name === supplierName)) continue
        list.push({
          name: supplierName,
          distanceMi: query.data.masterDistances?.[masterId] ?? null,
          price: query.data.masterPrices?.[masterId] ?? null,
        })
        target.set(masterId, list)
      }
    }
    for (const [masterId, list] of backorderMap.entries()) {
      if (!map.has(masterId)) map.set(masterId, list)
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const pa = a.price ?? Infinity
        const pb = b.price ?? Infinity
        if (pa !== pb) return pa - pb
        return (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity)
      })
    }
    return map
  }, [allPagesStockQueries])

  // Hydrate the in-stock supplier list with the per-supplier live prices
  // that landed in `livePriceByMaster`. Without this, the card's primary
  // in-stock path reads `inStockSuppliers[0].price` (populated from the
  // stock-check response using RawProductPrice.price at query time) and
  // never sees the refreshed number — my `product.lowestPrice` override
  // alone was dead code on the common in-stock branch.
  //
  // For each master we replace every entry whose supplier has a live
  // price and re-sort by price ascending (nearest-branch tie-breaker),
  // matching the source maps' invariant. Entries whose supplier didn't
  // return a live price for the master keep their stored value.
  const hydratedInStockSuppliersByMaster = useMemo(() => {
    const source = isAllPagesReady
      ? allPagesInStockSuppliersByMaster
      : inStockSuppliersByMaster
    if (livePriceByMaster.size === 0) return source

    const patched = new Map<
      string,
      Array<{ name: string; distanceMi: number | null; price: number | null }>
    >()
    for (const [masterId, suppliers] of source.entries()) {
      const live = livePriceByMaster.get(masterId)
      if (live === undefined) {
        patched.set(masterId, suppliers)
        continue
      }
      const updated = suppliers.map((entry) => {
        // Distinguish the three possible outcomes for this supplier:
        //   undefined  → didn't respond for this master → leave stored price
        //   null       → responded with no live price   → clear to null so
        //                the primary card slot doesn't advertise a stale
        //                stored number for an in-stock supplier whose raws
        //                are now unavailable
        //   number     → responded with a live price    → swap it in
        const livePrice = live.pricesBySupplierName.get(entry.name)
        if (livePrice === undefined) return entry
        return { ...entry, price: livePrice }
      })
      updated.sort((a, b) => {
        const pa = a.price ?? Infinity
        const pb = b.price ?? Infinity
        if (pa !== pb) return pa - pb
        return (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity)
      })
      patched.set(masterId, updated)
    }
    return patched
  }, [
    isAllPagesReady,
    allPagesInStockSuppliersByMaster,
    inStockSuppliersByMaster,
    livePriceByMaster,
  ])

  // In-stock products across all pages, with optional price/distance sort.
  // All data is already in memory — no new API calls.
  const inStockProducts = useMemo(() => {
    if (!isAllPagesReady) return []
    const filtered = allProductsFlat.filter(
      (p) => allPagesStockStateByMaster.get(p.id) === 'in_stock',
    )
    if (urlSort === 'price') {
      return [...filtered].sort(
        (a, b) => priceForSort(a) - priceForSort(b),
      )
    }
    if (urlSort === 'distance') {
      return [...filtered].sort((a, b) => {
        const da = allPagesNearestDistanceByMaster.get(String(a.id)) ?? Infinity
        const db = allPagesNearestDistanceByMaster.get(String(b.id)) ?? Infinity
        return da - db
      })
    }
    return filtered
  }, [isAllPagesReady, allProductsFlat, allPagesStockStateByMaster, urlSort, allPagesNearestDistanceByMaster, priceForSort])

  // Page slice for client-side in-stock pagination (shares urlPage with normal pagination)
  const inStockPageProducts = useMemo(
    () =>
      inStockProducts.slice(
        (urlPage - 1) * IN_STOCK_PAGE_SIZE,
        urlPage * IN_STOCK_PAGE_SIZE,
      ),
    [inStockProducts, urlPage],
  )

  // Product count shown in the result header. Returns null when the toggle is ON
  // but isAllPagesReady is still false — in that window the total from the backend
  // is meaningless (it includes out-of-stock items) so we show a loading label instead.
  const displayCount: number | null = useMemo(() => {
    if (showInStockOnly && isAllPagesReady) return inStockProducts.length
    if (
      !showInStockOnly &&
      ((urlSort === 'price' && allPagesFetched) || (urlSort === 'distance' && isAllPagesReady))
    )
      return allProductsFlat.length
    if (!showInStockOnly) return totalProducts
    return null
  }, [
    showInStockOnly,
    isAllPagesReady,
    inStockProducts,
    allPagesFetched,
    allProductsFlat,
    totalProducts,
    urlSort,
  ])

  // Global price sort across all pages — only active when all pages are fetched
  // and toggle is OFF. Without this, price sort only re-orders the current page's
  // 20 products, so a cheaper product on page 3 would never surface to page 1.
  const allPagesPriceSorted = useMemo(() => {
    if (!allPagesFetched || urlSort !== 'price') return []
    return [...allProductsFlat].sort((a, b) => {
      const far = farBranchLast(a, b)
      if (far !== 0) return far
      return priceForSort(a) - priceForSort(b)
    })
  }, [allPagesFetched, allProductsFlat, urlSort, priceForSort])

  const allPagesPricePageProducts = useMemo(
    () =>
      allPagesPriceSorted.slice(
        (urlPage - 1) * IN_STOCK_PAGE_SIZE,
        urlPage * IN_STOCK_PAGE_SIZE,
      ),
    [allPagesPriceSorted, urlPage],
  )

  // Global distance sort across all pages — mirrors allPagesPriceSorted but
  // uses allPagesNearestDistanceByMaster. Requires isAllPagesReady (not just
  // allPagesFetched) because distances come from the stock-check responses.
  const allPagesDistanceSorted = useMemo(() => {
    if (!isAllPagesReady || urlSort !== 'distance') return []
    return [...allProductsFlat].sort((a, b) => {
      const far = farBranchLast(a, b)
      if (far !== 0) return far
      const da = allPagesNearestDistanceByMaster.get(String(a.id)) ?? Infinity
      const db = allPagesNearestDistanceByMaster.get(String(b.id)) ?? Infinity
      return da - db
    })
  }, [isAllPagesReady, allProductsFlat, urlSort, allPagesNearestDistanceByMaster])

  const allPagesDistancePageProducts = useMemo(
    () =>
      allPagesDistanceSorted.slice(
        (urlPage - 1) * IN_STOCK_PAGE_SIZE,
        urlPage * IN_STOCK_PAGE_SIZE,
      ),
    [allPagesDistanceSorted, urlPage],
  )

  // ─── Branch prioritisation (DEV-50739 SP-003) — derived state ────────────
  // (Selection / explore state is read further up, before the analytics
  // effect that reports it.)

  // Current-page ranking — available as soon as each supplier's stock
  // answer lands, so products jump up progressively (same as the pills).
  const priorityInfoByMaster = useMemo(
    () => buildPriorityInfoByMaster(stockQueries, prioritisedBranchIds),
    [stockQueries, prioritisedBranchIds],
  )
  // All-pages ranking — once the background prefetch has settled, the
  // split runs over the whole result set so a match on page 3 surfaces on
  // page 1 (mirrors the all-pages price / distance sorts).
  const allPagesPriorityInfoByMaster = useMemo(
    () =>
      isAllPagesReady
        ? buildPriorityInfoByMaster(allPagesStockQueries, prioritisedBranchIds)
        : new Map<string, BranchPriorityInfo>(),
    [isAllPagesReady, allPagesStockQueries, prioritisedBranchIds],
  )

  // Live price per raw listing id, merged across the per-supplier price
  // refreshes. Lets a linked card price the exact listing it shows once the
  // refresh lands, instead of the stock-check's stored snapshot (the two run
  // concurrently, so the snapshot can predate the refresh).
  const liveRawPriceById = useMemo(() => {
    const map = new Map<string, number | null>()
    for (const query of livePriceQueries) {
      const rawPrices = query.data?.rawPrices
      if (!rawPrices) continue
      for (const [rawId, price] of Object.entries(rawPrices)) map.set(rawId, price)
    }
    return map
  }, [livePriceQueries])
  // Pack size each live price was quoted under (CEF can change it), so a
  // linked card steps and validates by the multiple add-to-cart will check.
  const liveRawQuantityMultipleById = useMemo(() => {
    const map = new Map<string, number>()
    for (const query of livePriceQueries) {
      const multiples = query.data?.rawQuantityMultiples
      if (!multiples) continue
      for (const [rawId, multiple] of Object.entries(multiples)) map.set(rawId, multiple)
    }
    return map
  }, [livePriceQueries])

  const activePriorityInfo = isAllPagesReady
    ? allPagesPriorityInfoByMaster
    : priorityInfoByMaster

  // Supplier + branch names of each ticked branch, for its linked cards.
  const branchNamesById = useMemo(
    () => new Map(selectedBranches.map((branch) => [branch.branchId, branch])),
    [selectedBranches],
  )

  // Full ordered set for the active mode, expanded into linked product cards
  // (QA-001): one per listing at each ticked branch, in-stock first, then
  // backorder-only, then the grouped cards of everything else. Only built
  // when a pill is ticked and every page + stock answer is in.
  const prioritisedAllPages = useMemo((): PrioritisedResultEntry<MasterProduct>[] => {
    if (!hasBranchPriority || !isAllPagesReady) return []
    const base = showInStockOnly
      ? inStockProducts
      : urlSort === 'price'
        ? allPagesPriceSorted
        : urlSort === 'distance'
          ? allPagesDistanceSorted
          : allProductsFlat
    return buildPrioritisedEntries(
      base,
      allPagesPriorityInfoByMaster,
      branchNamesById,
      liveRawPriceById,
    )
  }, [
    hasBranchPriority,
    isAllPagesReady,
    showInStockOnly,
    urlSort,
    inStockProducts,
    allPagesPriceSorted,
    allPagesDistanceSorted,
    allProductsFlat,
    allPagesPriorityInfoByMaster,
    branchNamesById,
    liveRawPriceById,
  ])

  // The page of cards actually rendered. Without a ticked pill this is the
  // pre-existing selection, untouched. With one: the all-pages expansion
  // once ready, else the current page expanded with what has resolved so far.
  const basePageProducts =
    showInStockOnly && isAllPagesReady
      ? inStockPageProducts
      : !showInStockOnly && urlSort === 'price' && allPagesFetched
        ? allPagesPricePageProducts
        : !showInStockOnly && urlSort === 'distance' && isAllPagesReady
          ? allPagesDistancePageProducts
          : displayResults
  const visibleEntries = useMemo((): PrioritisedResultEntry<MasterProduct>[] => {
    if (!hasBranchPriority) {
      return basePageProducts.map((product) => ({ kind: 'master', product }))
    }
    if (isAllPagesReady) {
      return prioritisedAllPages.slice(
        (urlPage - 1) * IN_STOCK_PAGE_SIZE,
        urlPage * IN_STOCK_PAGE_SIZE,
      )
    }
    return buildPrioritisedEntries(
      basePageProducts,
      priorityInfoByMaster,
      branchNamesById,
      liveRawPriceById,
    )
  }, [
    hasBranchPriority,
    isAllPagesReady,
    prioritisedAllPages,
    urlPage,
    basePageProducts,
    priorityInfoByMaster,
    branchNamesById,
    liveRawPriceById,
  ])
  // Linked cards inside the set actually being shown. Decides the section
  // headings — on every page once all pages are in — and, at zero, the
  // SP-004 "no matches" message. Counts cards, not products.
  const prioritisedCount = useMemo(() => {
    if (!hasBranchPriority) return 0
    const pool = isAllPagesReady ? prioritisedAllPages : visibleEntries
    return pool.filter((entry) => entry.kind === 'linked').length
  }, [hasBranchPriority, isAllPagesReady, prioritisedAllPages, visibleEntries])
  // Each linked card's place among its supplier's linked cards (1 = first),
  // counted through the whole list, for the quick add analytics event. Empty
  // until every page is in: a count from one page would restart at 1 on each
  // page, so a quick add made meanwhile records no position rather than a
  // wrong one.
  const supplierPositionByCardKey = useMemo(() => {
    const positions = new Map<string, number>()
    if (!hasBranchPriority || !isAllPagesReady) return positions
    const countBySupplier = new Map<string, number>()
    for (const entry of prioritisedAllPages) {
      if (entry.kind !== 'linked') continue
      const position = (countBySupplier.get(entry.card.supplierId) ?? 0) + 1
      countBySupplier.set(entry.card.supplierId, position)
      positions.set(entry.card.key, position)
    }
    return positions
  }, [hasBranchPriority, isAllPagesReady, prioritisedAllPages])
  // Size of the set the headings describe: the whole result set once all
  // pages are in, else the current page (same pool as prioritisedCount).
  const headingPoolCount = isAllPagesReady ? prioritisedAllPages.length : visibleEntries.length
  const otherCount = Math.max(0, headingPoolCount - prioritisedCount)
  // SP-004: nothing in the settled result set can come from a ticked branch.
  // Shown only once every page and stock answer is in, so it never flashes
  // while a slow supplier could still produce a match (the wireframe had
  // instant data and needed no such wait). No further "confirmed" gate —
  // the story and wireframe show the message whenever nothing matched.
  const showNoMatches =
    hasBranchPriority &&
    isAllPagesReady &&
    prioritisedAllPages.length > 0 &&
    prioritisedCount === 0

  const tickedBranchLabel =
    selectedBranches.length === 1
      ? `${selectedBranches[0].supplierName} · ${selectedBranches[0].branchName}`
      : 'your selected branches'

  // Client-side pagination applies whenever the all-pages split is live.
  const usesPrioritisedPagination = hasBranchPriority && isAllPagesReady

  // Ticking / unticking a pill re-splits the list, so start from page 1
  // rather than risk landing on a page that no longer exists.
  const handleBranchPriorityChange = () => {
    if (!searchParams.has('page')) return
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('page')
    setSearchParams(newParams, { replace: true })
  }

  // PostHog: one supplier_branch_filter_applied per branch just ticked.
  // A tick is queued and sent once every page and stock answer for the
  // current search has settled, so matched_count (worked out for THAT
  // branch from the settled stock answers) is complete rather than a
  // partial count from whatever had arrived by tap time. If the search
  // moves on first, the queued ticks are sent with matched_count null.
  const filterSearchKey = searchKey(
    debouncedQuery.trim(),
    categoryIdForSearch,
    urlNoCorrection,
    session?.postcode,
  )
  // Each queued tick carries the full analytics context captured at tap
  // time, so a tick emitted later (after settling, after the search moved
  // on, or on unmount) always describes the search it was made on.
  interface PendingFilterChange {
    searchKey: string
    branchIds: Set<string>
    // In-stock filter state at tap time: with it on, backorder matches are
    // not in the "Showing first" group, so the count must exclude them.
    inStockOnly: boolean
    payload: Omit<
      Parameters<typeof trackSupplierBranchFilterApplied>[0],
      'priorityResultsCount'
    >
  }
  const pendingFilterChangesRef = useRef<PendingFilterChange[]>([])
  const handleSelectionApplied = (selected: BasketBranch[]) => {
    const activeQuery = debouncedQuery.trim()
    // A category-only browse emits no search_performed, so there is no
    // search to correlate with — send null rather than mint a phantom id.
    const searchId = activeQuery ? getOrMintSearchId(filterSearchKey) : null
    pendingFilterChangesRef.current.push({
      searchKey: filterSearchKey,
      branchIds: new Set(selected.map((branch) => branch.branchId)),
      inStockOnly: showInStockOnly,
      payload: {
        searchId,
        supplierIds: [...new Set(selected.map((branch) => branch.supplierId))].join(', '),
        supplierNames: [...new Set(selected.map((branch) => branch.supplierName))].join(', '),
        branchIds: selected.map((branch) => branch.branchId).join(', '),
        branchNames: selected
          .map((branch) => `${branch.supplierName} · ${branch.branchName}`)
          .join(', '),
        selectedCount: selected.length,
        query: activeQuery,
        totalResultsCount: totalProducts,
        postcode: session?.postcode ?? null,
      },
    })
  }
  useEffect(() => {
    const pending = pendingFilterChangesRef.current
    if (pending.length === 0) return
    // Changes queued for an earlier search can never get a complete count.
    const stale = pending.filter((change) => change.searchKey !== filterSearchKey)
    const current = pending.filter((change) => change.searchKey === filterSearchKey)
    for (const change of stale) {
      trackSupplierBranchFilterApplied({ ...change.payload, priorityResultsCount: null })
    }
    if (!isAllPagesReady) {
      pendingFilterChangesRef.current = current
      return
    }
    for (const change of current) {
      // Size of the "Showing first" group for that selection: results any
      // of its branches can supply, counted over the same pool the page
      // shows (all results, or only in-stock ones when that filter was on).
      const infoByMaster = buildPriorityInfoByMaster(allPagesStockQueries, change.branchIds)
      const priorityResultsCount = allProductsFlat.filter(
        (product) =>
          infoByMaster.has(String(product.id)) &&
          (!change.inStockOnly || allPagesStockStateByMaster.get(product.id) === 'in_stock'),
      ).length
      trackSupplierBranchFilterApplied({ ...change.payload, priorityResultsCount })
    }
    pendingFilterChangesRef.current = []
  }, [
    filterSearchKey,
    isAllPagesReady,
    allPagesStockQueries,
    allProductsFlat,
    allPagesStockStateByMaster,
  ])
  // Leaving the page (product / basket / elsewhere) before stock settled:
  // flush what is queued with an unknown count rather than lose the change.
  useEffect(
    () => () => {
      for (const change of pendingFilterChangesRef.current) {
        trackSupplierBranchFilterApplied({ ...change.payload, priorityResultsCount: null })
      }
      pendingFilterChangesRef.current = []
    },
    [],
  )

  if (!session) {
    return null
  }

  return (
    <EngineerLayout>
      {/* Location bar removed — postcode pill now lives in the shared
       * EngineerHeader (DEV-48214 §5.4A). Clicking the pill navigates back to
       * JobSetup, replacing the old "Change Location" affordance. */}

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Input
              ref={searchInputRef}
              // Opt this one field out of session-replay input masking so
              // replays show the real search term (maskInputFn in posthog.ts).
              // The Input component spreads {...props}, so this reaches the DOM.
              data-replay-unmask="true"
              placeholder={
                activeCategory
                  ? `Search in ${activeCategory.name}...`
                  : 'Search for products...'
              }
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value)
                if (!isSuggestOpen) setIsSuggestOpen(true)
              }}
              onFocus={() => setIsSuggestOpen(true)}
              onBlur={() => {
                // Defer the close so a click on a suggestion (mousedown)
                // can fire before the dropdown unmounts.
                window.setTimeout(() => setIsSuggestOpen(false), 100)
              }}
              onKeyDown={handleSearchInputKeyDown}
              // Both controls sit on the RIGHT of the field: engineers expect
              // the magnifier there, and on mobile it reads as the search key.
              // onMouseDown is suppressed on both so the field keeps focus and
              // the blur-deferred suggestion close doesn't swallow the tap.
              rightIcon={
                <div className="-mr-2 flex items-center">
                  {searchInput.length > 0 && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={handleClearSearchInput}
                      // Full 44×44 tap target (SB-06) that doesn't overlap the
                      // magnifier's: the spare tap area extends left into the
                      // empty end of the field. The inner pill keeps the icon
                      // and its pressed grey exactly where they were before.
                      className="group flex h-11 w-11 items-center justify-end text-gray-400 transition-colors hover:text-gray-600"
                    >
                      <span className="pointer-events-none -mr-1.5 flex h-11 w-8 items-center justify-center rounded-full transition-colors group-active:bg-gray-100">
                        <X className="h-5 w-5" />
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="Search"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setIsSuggestOpen(false)
                      handleSearchSubmit()
                    }}
                    className="flex h-11 w-11 items-center justify-center rounded-full text-brand-primary transition-colors active:bg-gray-100"
                  >
                    <SearchIcon className="h-5 w-5" />
                  </button>
                </div>
              }
              // Right padding tracks how many controls are showing, so the
              // placeholder doesn't stop short while the field is empty.
              className={searchInput.length > 0 ? 'h-14 pr-24' : 'h-14 pr-16'}
              autoComplete="off"
              aria-autocomplete="list"
              aria-expanded={isSuggestOpen && orderedSuggestions.length > 0}
              aria-controls="search-suggestions-listbox"
            />
            {isSuggestOpen && searchInput.trim().length >= 2 && (
              <SearchSuggestionsDropdown
                suggestions={orderedSuggestions}
                query={searchInput}
                activeIndex={activeSuggestIndex}
                onSelect={handleSuggestionSelect}
                onHoverIndex={setActiveSuggestIndex}
                isLoading={isSuggestLoading}
              />
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">Press Enter to search</p>
        </div>

        {/* "Show first" pills (DEV-50739 SP-001): one per supplier+branch in
            the basket. Shown only once a search / category browse has
            returned results — not on the landing view, while loading, or
            on the no-results screen (nothing to prioritise). Ticks are kept
            while hidden and apply again with the next results. */}
        {results.length > 0 && !isSearching && (
          <BranchPriorityPills
            onSelectionChange={handleBranchPriorityChange}
            onSelectionApplied={handleSelectionApplied}
          />
        )}

        {/* Did-you-mean correction notice (Epic 3). Renders only when the
            backend's phrase suggester rewrote the user's query into a
            better-spelled alternative. Clicking the revert link re-runs
            the search with ?no_correction=1 so the literal typed query is
            used unchanged. */}
        {searchQueryInfo &&
          (searchQueryInfo.wasCorrected ||
            searchQueryInfo.synonymFuzzyCorrected ||
            searchQueryInfo.keywordCategoryFuzzyCorrected) &&
          !isSearching &&
          debouncedQuery && (
            <div
              role="status"
              aria-live="polite"
              className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 flex flex-wrap items-center gap-x-2 gap-y-1"
            >
              <span>
                Showing results for{' '}
                <strong className="font-semibold">
                  {searchQueryInfo.wasCorrected
                    ? searchQueryInfo.correctionHighlighted
                      ? renderHighlighted(searchQueryInfo.correctionHighlighted)
                      : searchQueryInfo.used
                    : searchQueryInfo.synonymFuzzyCorrected
                    ? searchQueryInfo.synonymFuzzyUsed
                    : searchQueryInfo.keywordCategoryFuzzyUsed}
                </strong>
              </span>
              <span className="text-amber-800">
                Search instead for{' '}
                <button
                  type="button"
                  className="underline hover:no-underline font-medium"
                  onClick={() => {
                    const literal = searchQueryInfo.original
                    setSearchInput(literal)
                    setDebouncedQuery(literal)
                    const newParams = new URLSearchParams(searchParams)
                    if (literal) {
                      newParams.set('q', literal)
                    } else {
                      newParams.delete('q')
                    }
                    newParams.set('no_correction', '1')
                    newParams.delete('page')
                    setSearchParams(newParams, { replace: true })
                  }}
                >
                  <code className="font-mono text-amber-900">
                    {searchQueryInfo.original}
                  </code>
                </button>
              </span>
            </div>
          )}

        {/* Loading State — skeleton cards while the search query is in flight. */}
        {isSearching && (
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div
                key={idx}
                className="w-full bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 animate-pulse"
              >
                <div className="flex gap-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-xl flex-shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                    <div className="flex items-center justify-between pt-2">
                      <div className="h-5 bg-gray-200 rounded w-20" />
                      <div className="h-4 w-4 bg-gray-200 rounded" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State - Show Recent Purchases + category browser. Hidden once a
            category browse is active (empty q + category_id) so the listing shows. */}
        {!debouncedQuery && categoryIdForSearch === null && !isSearching && (
          <div className="pb-4">
            {recentPurchases.length > 0 && (
              <div className="mb-8">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <History className="w-5 h-5 text-brand-primary" />
                    <h2 className="text-gray-900 text-xl font-bold">Recently purchased</h2>
                  </div>
                  <p className="text-gray-500 text-sm">
                    Quick re-order from your previous purchases
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {recentPurchases.map((purchase: RecentPurchase) => (
                    <div
                      key={purchase.id}
                      className="bg-white rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all overflow-hidden"
                    >
                      {/* Product Image - Clickable */}
                      <button
                        onClick={() =>
                          navigate(`/${tenantSlug}/engineer/supplier-product/${purchase.productId}`)
                        }
                        className="w-full bg-gray-100 h-28 flex items-center justify-center overflow-hidden"
                      >
                        {purchase.imageUrl ? (
                          <img
                            src={purchase.imageUrl}
                            alt={purchase.productName}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden') }}
                          />
                        ) : null}
                        <div className={purchase.imageUrl ? 'hidden flex flex-col items-center justify-center' : 'flex flex-col items-center justify-center'}>
                          <Package className="w-10 h-10 text-gray-300" />
                        </div>
                      </button>

                      {/* Product Info */}
                      <div className="p-4">
                        <button
                          onClick={() =>
                            navigate(`/${tenantSlug}/engineer/supplier-product/${purchase.productId}`)
                          }
                          className="text-left w-full mb-3"
                        >
                          <h3 className="text-gray-900 font-bold text-sm mb-2 line-clamp-2">
                            {purchase.productName}
                          </h3>

                          {/* Supplier + Distance */}
                          <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-2">
                            <span className="font-medium text-gray-500">{purchase.supplierName}</span>
                            {purchase.distanceMiles != null && (
                              <>
                                <span>•</span>
                                <span>{purchase.distanceMiles.toFixed(1)} mi</span>
                              </>
                            )}
                          </div>

                          {/* Stock Status */}
                          <div className="mb-2">
                            <span
                              className={`flex items-center gap-1.5 text-[11px] font-medium ${
                                purchase.stockStatus === 'in_stock'
                                  ? 'text-green-600'
                                  : purchase.stockStatus === 'low_stock'
                                    ? 'text-amber-600'
                                    : 'text-red-600'
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  purchase.stockStatus === 'in_stock'
                                    ? 'bg-green-500'
                                    : purchase.stockStatus === 'low_stock'
                                      ? 'bg-amber-500'
                                      : 'bg-red-500'
                                }`}
                              />
                              {purchase.stockStatus === 'in_stock'
                                ? IN_STOCK_LABEL
                                : purchase.stockStatus === 'low_stock'
                                  ? 'Low stock'
                                  : 'Out of stock'}
                            </span>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-brand-primary font-bold">
                              £{purchase.price.toFixed(2)}
                            </span>
                            <div className="flex items-center gap-1 text-gray-400 text-xs">
                              <Clock className="w-3 h-3" />
                              {formatTimeAgo(purchase.purchasedAt)}
                            </div>
                          </div>
                        </button>

                        {/* Add to Cart Button — feature not implemented yet.
                         * Kept as a disabled placeholder using the unified
                         * AddToCartButton (DEV-48214 §5.4C) so it will theme
                         * correctly once wired up. */}
                        <AddToCartButton
                          state="add"
                          onClick={() => { /* placeholder; see DEV-48213 */ }}
                          disabled
                          size="sm"
                          hideIcon
                          className="w-full"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* View All Link */}
                <div className="text-center">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/${tenantSlug}/engineer/orders`)}
                  >
                    <History className="w-4 h-4 mr-2" />
                    View all purchase history
                  </Button>
                </div>
              </div>
            )}
            <CategoryBrowse
              onSelectCategory={handleBrowseCategorySelect}
              enabled={!!session}
              browseCategoryId={browseCategoryId}
              setBrowseCategoryId={setBrowseCategoryId}
            />
          </div>
        )}

        {/* Search Results (keyword search OR category browse listing) */}
        {(debouncedQuery || categoryIdForSearch !== null) && !isSearching && results.length > 0 && (
          <div className="space-y-4">
            <div className="flex flex-col gap-2.5">
              {/* Row 1: result count + active category scope chip */}
              {/* Single line, never wraps: the count is fixed width and the pill
                  shrinks, truncating the category name with "…" to whatever
                  space is left. Adapts to any viewport / count length instead
                  of relying on a hard-coded max-width. */}
              <div className="flex min-w-0 items-center gap-2 text-gray-500 text-sm">
                <Package className="w-4 h-4 shrink-0" />
                {displayCount !== null ? (
                  <span className="shrink-0 whitespace-nowrap">
                    <span className="font-medium text-gray-600">{displayCount}</span>
                    {' '}product{displayCount !== 1 ? 's' : ''} found
                  </span>
                ) : (
                  <span className="shrink-0 whitespace-nowrap">Counting available products…</span>
                )}
                {categoryIdForSearch !== null && (
                  // One pill, two tap targets: the label widens the scope by one
                  // level (mock behaviour); the × clears the filter entirely.
                  <span className="inline-flex min-h-9 min-w-0 items-stretch rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-700">
                    <button
                      type="button"
                      onClick={widenScope}
                      title="Widen one level"
                      className="inline-flex min-w-0 items-center gap-1.5 rounded-l-full py-1.5 pl-3 pr-1.5 transition-colors hover:text-brand-primary"
                    >
                      <span className="shrink-0 text-gray-400">in</span>
                      <span className="min-w-0 max-w-[12rem] truncate">
                        {scopedCategory?.name ?? 'selected category'}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={clearCategoryScope}
                      title="Clear category filter"
                      aria-label={`Clear category filter: ${scopedCategory?.name ?? 'selected category'}`}
                      className="inline-flex shrink-0 items-center rounded-r-full py-1.5 pl-1 pr-2.5 text-gray-400 transition-colors hover:text-brand-primary"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </span>
                )}
              </div>

              {/* Row 2: sort + in-stock toggle — result modifiers grouped together */}
              <div className="flex items-center gap-3">
                {/* Sort by — flex-1 so it fills available width */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-xs text-gray-500 select-none shrink-0">Sort by</span>
                  <select
                    value={urlSort}
                    onChange={(e) => handleSortChange(e.target.value as SortOption)}
                    className="flex-1 min-w-0 max-w-48 h-9 px-3 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary appearance-none bg-no-repeat pr-8"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundSize: '1.25em 1.25em',
                    }}
                  >
                    <option value="relevance">Relevance</option>
                    <option value="price">Price: Low to High</option>
                    <option value="distance" disabled={!isStockCheckComplete}>Distance: Nearest</option>
                  </select>
                </div>

                {/* In stock only toggle */}
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-500 select-none">Available</span>
                  <button
                    role="switch"
                    aria-checked={showInStockOnly}
                    aria-label="Show available products only"
                    disabled={!isAllPagesReady}
                    onClick={() => {
                      const next = !showInStockOnly
                      setShowInStockOnly(next)
                      if (next) {
                        sessionStorage.setItem('inStockFilter', '1')
                      } else {
                        sessionStorage.removeItem('inStockFilter')
                      }
                      const newParams = new URLSearchParams(searchParams)
                      newParams.delete('page')
                      setSearchParams(newParams, { replace: true })
                    }}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-1 ${
                      !isAllPagesReady
                        ? 'opacity-40 cursor-not-allowed bg-gray-200'
                        : showInStockOnly
                          ? 'bg-emerald-500'
                          : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        showInStockOnly ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {showNoMatches && <BranchPriorityNoMatches branchLabel={tickedBranchLabel} />}
            {visibleEntries.map((entry, productIndex: number) => {
              const { product } = entry
              // Section headings (SP-003): "Showing first" above the first
              // linked card, "Other suppliers and branches" above the
              // first grouped card — including at the top of a
              // later page that holds only non-matches, and when nothing
              // matched at all (under the "No matches" card the whole list
              // is "other", and the heading says so with its count).
              const isPrioritised = entry.kind === 'linked'
              const previousEntry = productIndex > 0 ? visibleEntries[productIndex - 1] : null
              const previousPrioritised = previousEntry?.kind === 'linked'
              // While stock answers are still arriving nothing has matched
              // yet, so the zero-match "Other…" heading waits for the same
              // settled state the "No matches" card uses; with at least one
              // match the headings show at once.
              const headingsReady = prioritisedCount > 0 || isAllPagesReady
              const sectionHeading = !hasBranchPriority || !headingsReady
                ? null
                : isPrioritised && productIndex === 0
                  ? `Showing first (${prioritisedCount})`
                  : !isPrioritised && (productIndex === 0 || previousPrioritised)
                    ? `Other suppliers and branches (${otherCount})`
                    : null
              // Override with the merged live price + winning supplier
              // only once every relevant supplier has responded (see
              // `livePriceByMaster`). Both fields swap together so the
              // shown number and the shown supplier can't drift apart
              // when the winner shifts during hydration. Applied at
              // render-only so downstream sort / stock logic continues
              // to see the stable stored `lowestPrice`.
              const liveEntry = livePriceByMaster.get(String(product.id))
              // When live settled with no price (`lowestPrice === null`),
              // zero out the number and drop the supplier attribution so
              // the card renders a hyphen instead of continuing to
              // advertise a soft-deleted listing.
              // 1-indexed position within the full result list across all
              // pages so the analytics signal reflects how deep the user
              // actually scrolled, not just their position on the current page.
              const position = (urlPage - 1) * 20 + productIndex + 1
              if (entry.kind === 'linked') {
                return (
                  <div key={`linked:${entry.card.key}`}>
                    {sectionHeading !== null && (
                      <p className="mb-3 text-xs font-semibold text-gray-500">{sectionHeading}</p>
                    )}
                    <LinkedProductCard
                      product={product}
                      card={entry.card}
                      position={position}
                      supplierPosition={supplierPositionByCardKey.get(entry.card.key) ?? null}
                      isPriceRefreshing={priceRefreshingByMaster.get(String(product.id)) === true}
                      postcode={session?.postcode ?? null}
                      livePackSize={liveRawQuantityMultipleById.get(entry.card.rawId) ?? null}
                      onOpen={handleLinkedCardClick}
                    />
                  </div>
                )
              }
              const displayProduct =
                liveEntry !== undefined
                  ? {
                      ...product,
                      lowestPrice: liveEntry.lowestPrice ?? 0,
                      lowestPriceSupplierName:
                        liveEntry.lowestPrice != null
                          ? liveEntry.lowestPriceSupplierName
                          : null,
                    }
                  : product
              return (
                <div key={product.id}>
                  {sectionHeading !== null && (
                    <p
                      className={`mb-3 text-xs font-semibold text-gray-500 ${productIndex === 0 ? '' : 'mt-5'}`}
                    >
                      {sectionHeading}
                    </p>
                  )}
                  <SearchProductCard
                    product={displayProduct}
                    position={position}
                    stockState={(isAllPagesReady ? allPagesStockStateByMaster : stockStateByMaster).get(product.id) ?? 'checking'}
                    inStockSuppliers={
                      hydratedInStockSuppliersByMaster.get(String(product.id)) ?? []
                    }
                    isPriceRefreshing={priceRefreshingByMaster.get(String(product.id)) === true}
                    onClick={handleProductCardClick}
                  />
                </div>
              )
            })}

            {usesPrioritisedPagination ? (
              <Pagination
                currentPage={urlPage}
                totalItems={prioritisedAllPages.length}
                itemsPerPage={IN_STOCK_PAGE_SIZE}
                onPageChange={handlePageChange}
                hasNextPage={urlPage * IN_STOCK_PAGE_SIZE < prioritisedAllPages.length}
                hasPrevPage={urlPage > 1}
                isLoading={false}
                className="mt-6"
              />
            ) : showInStockOnly && !isAllPagesReady ? (
              <Pagination
                currentPage={1}
                totalItems={0}
                itemsPerPage={IN_STOCK_PAGE_SIZE}
                onPageChange={handlePageChange}
                hasNextPage={false}
                hasPrevPage={false}
                isLoading={true}
                className="mt-6"
              />
            ) : showInStockOnly && isAllPagesReady ? (
              <Pagination
                currentPage={urlPage}
                totalItems={inStockProducts.length}
                itemsPerPage={IN_STOCK_PAGE_SIZE}
                onPageChange={handlePageChange}
                hasNextPage={urlPage * IN_STOCK_PAGE_SIZE < inStockProducts.length}
                hasPrevPage={urlPage > 1}
                isLoading={false}
                className="mt-6"
              />
            ) : (
              <Pagination
                currentPage={urlPage}
                totalItems={totalProducts}
                itemsPerPage={20}
                onPageChange={handlePageChange}
                hasNextPage={hasNextPage}
                hasPrevPage={hasPrevPage}
                isLoading={isSearching}
                className="mt-6"
              />
            )}

            {/* Idea 2: subtle "Can't find what you're looking for?" prompt.
                Sits beneath the result list so it only catches the eye of
                users who have already scanned through the page. */}
            <div className="flex justify-center pt-2 pb-1">
              <button
                type="button"
                onClick={() => setShowFeedbackModal(true)}
                className="text-sm text-gray-500 hover:text-brand-primary underline underline-offset-2 transition-colors"
              >
                Can't find what you're looking for?
              </button>
            </div>
          </div>
        )}

        {/* No Results (keyword search OR empty category browse) */}
        {(debouncedQuery || categoryIdForSearch !== null) && !isSearching && results.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6">
              <SearchIcon className="w-10 h-10 text-gray-300" />
            </div>
            <h2 className="text-gray-900 text-xl font-bold mb-3 text-center">
              {debouncedQuery
                ? `No results found for "${debouncedQuery}"`
                : `No products in ${scopedCategory?.name ?? 'this category'}`}
            </h2>
            <p className="text-gray-500 text-base text-center mb-6">
              {debouncedQuery
                ? 'Try searching for something else'
                : 'Try another category or search by product name'}
            </p>
            {categoryIdForSearch !== null && (
              <Button
                variant="outline"
                size="md"
                onClick={clearCategoryScope}
                className="mb-3"
              >
                Search all categories
              </Button>
            )}
            {/* Secondary feedback affordance — a subtle text link (same style
                as the "Can't find what you're looking for?" prompt in the
                results view) so it sits below the primary recovery action
                rather than competing with it. */}
            <button
              type="button"
              onClick={() => setShowFeedbackModal(true)}
              className="text-sm text-gray-500 hover:text-brand-primary underline underline-offset-2 transition-colors"
            >
              Tell us what you need
            </button>
          </div>
        )}
      </main>

      {/* Search feedback modal — triggered by the "Can't find?" CTA. */}
      <SearchFeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        query={debouncedQuery}
        onSubmit={handleFeedbackSubmit}
      />
    </EngineerLayout>
  )
}
